import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {forkJoin, Observable} from 'rxjs';
import {finalize, take} from 'rxjs/operators';
import {saveAs} from 'file-saver';
import {FileDTO} from '../../../../../models/FileDTO';
import {LookupValue} from '../../../../../types/lookup-value.enum';
import {CaseHospitalDTO} from '../../../../../models/case-hospital/CaseHospitalDTO';
import {LookupService} from '../../../../../../common/services/lookup.service';
import {NameValueDTO} from '../../../../../../common/models/NameValueDTO';
import {SharedService} from '../../../../../../common/services/shared-service';
import {FileUploadService} from '../../../../../../common/services/file-upload.service';
import {CaseHospitalFileLinkDTO} from '../../../../../models/case-hospital/CaseHospitalFileLinkDTO';
import {
  ConfirmationDialogComponent
} from '../../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';

enum Controls {
  DocumentationType = 'documentationTypeSelected',
  Description = 'freeTextDescription',
  WorkmanCompensationDocumentation = 'workmanCompensationDocumentation',
  RoadAccidentFundDocumentation = 'roadAccidentFundDocumentation',
}

enum DocumentationType {
  GeneralDocumentation = 'General Documentation',
  WorkmenCompensationDocumentation = 'Workmen’s Compensation Documentation',
  RoadAccidentFundDocumentation = 'Road Accident Fund Documentation',
}

@Component({
  selector: 'app-case-documentation-step',
  templateUrl: './case-documentation-step.component.html',
  styleUrls: [
    '../../../../../../../../styles/global/_add-view-edit-styles.scss',
    './case-documentation-step.component.scss'
  ]
})
export class CaseDocumentationStepComponent implements OnChanges, OnInit {
  readonly controls = Controls;
  readonly lookupValue = LookupValue;
  readonly documentationType = DocumentationType;

  @Input() data: CaseHospitalDTO;
  @Input() formGroup: FormGroup;
  @Input() isEditMode: boolean;
  @Input() caseHospitalId: number | bigint;
  @Output() uploadDocument: EventEmitter<CaseHospitalFileLinkDTO> = new EventEmitter<CaseHospitalFileLinkDTO>(null); // AdHoc requirement to upload on Edit Case `/document` endpoint
  @Output() deleteDocument: EventEmitter<any> = new EventEmitter<any>(null); // AdHoc requirement to upload on Edit Case `/document` endpoint

  disableUploadBtn = true;
  workmanCompensationDocumentationLookUpValues: NameValueDTO[];
  roadAccidentFundDocumentationLookUpValues: NameValueDTO[];

  // Note: (GD + WD + RD) concatenated list to be posted at BE
  caseHospitalFiles: CaseHospitalFileLinkDTO[] = [];
  generalDocumentation: CaseHospitalFileLinkDTO[] = [];
  workmanCompensationDocumentation: CaseHospitalFileLinkDTO[] = [];
  roadAccidentFundDocumentation: CaseHospitalFileLinkDTO[] = [];
  documentationTypeLookUpValues: string[] = Object.values(this.documentationType);
  fileUploadConfig: any = {
    uploadedFileData: null,
    /*
     Todo: This is never used because we can't remove on staying on same page on attachment upload
     if we clear it on `onFileUploadSuccess` than empty blob can't be converted into image.
     */
    clearFileData: true,
    description: null
  };

  private loggedInUserName: string;

  constructor(
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private fileUploadService: FileUploadService,
    private lookupService: LookupService,
    private sharedService: SharedService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEditMode) {
      this.isEditMode = changes.isEditMode.currentValue;
    }
    if (changes.formGroup) {
      this.formGroup = changes.formGroup.currentValue;
    }
    if (changes.data && changes.data.currentValue) {
      this.data = changes.data.currentValue;
      if (this.isEditMode) {
        this.setFormsForEdit(this.data);
      }
    }
  }

  ngOnInit(): void {
    this.initLookUpValues();
    this.initDocumentationStepFormControls();
    this.initSubscriptions();
  }

  initLookUpValues(): void {
    forkJoin([
      this.lookupService.getListOfLookupValues(this.lookupValue.WorkmanCompensationDocumentation),
      this.lookupService.getListOfLookupValues(this.lookupValue.RoadAccidentFundDocumentation)
    ])
      .pipe(
        take(1)
      )
      .subscribe({
        next: ([workmanCompensationDocumentation, roadAccidentFundDocumentation]) => {
          this.workmanCompensationDocumentationLookUpValues = workmanCompensationDocumentation;
          this.roadAccidentFundDocumentationLookUpValues = roadAccidentFundDocumentation;
        },
        error: (error) => {
          this.sharedService.openErrorDialog(error.message);
        }
      });
  }

  initDocumentationStepFormControls(): void {
    this.formGroup = this.formBuilder.group({
      [Controls.DocumentationType]: new FormControl(this.documentationType.GeneralDocumentation),
      [Controls.Description]: new FormControl(null, [Validators.required]),
      [Controls.WorkmanCompensationDocumentation]: new FormControl(null),
      [Controls.RoadAccidentFundDocumentation]: new FormControl(null)
    });
  }

  initSubscriptions(): void {
    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe(() => {
        // Note: Won't be using profile obj instead invoke `getLoggedInUserName` as username that is already set in service.
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });
  }

  downloadFile($event: any, uploadedFile: any): void {
    $event.preventDefault();
    const {name, extension, file} = uploadedFile.file;
    const blobData = this.fileUploadService.base64toBlob(file, 'data:application/octet-stream;base64');
    const blob = new Blob([blobData]);
    saveAs(blob, `${name.trim()}${extension}`);
  }

  clearFilesLinkDTOList(): void {
    this.removeAllCaseHospitalFileLinkDTO();
  }

  onInputChangedUpdateDescription(): void {
    const documentationTypeSelected = this.formGroup.controls[Controls.DocumentationType].value;
    const description = this.formGroup.controls[Controls.Description].value;

    // Note:
    // Description that will update config reference and will be used to pass free text description to upload in file upload
    this.fileUploadConfig.description = description;
    this.disableUploadBtn = !(documentationTypeSelected && description);
  }

  onWorkmenCompensationLookUpValueSelected(selectedValue: number): void {
    const matchFound = this.workmanCompensationDocumentationLookUpValues.find((item: NameValueDTO) => item.value === selectedValue);

    if (matchFound) {
      // Save Actual Description of NameValueDTO
      this.fileUploadConfig.description = matchFound.name;
    }
  }

  onRoadAccidentLookUpValueSelected(selectedValue: number): void {
    const matchFound = this.roadAccidentFundDocumentationLookUpValues.find((item: NameValueDTO) => item.value === selectedValue);
    if (matchFound) {
      // Save Actual Description of NameValueDTO
      this.fileUploadConfig.description = matchFound.name;
    }
  }

  onDocumentationTypeSelected(type: string): void {

    if (type === DocumentationType.GeneralDocumentation) {
      this.patchDefaultGeneralDocumentationValue();
      // Disabled until "User adds some description and then onBlur method will be invoked
      this.disableUploadBtn = true;
    }
    else if (type === DocumentationType.WorkmenCompensationDocumentation) {
      this.patchDefaultWorkmenDocumentationValue();
      this.disableUploadBtn = false;

      // Update `description` byRef for fileUploadConfig
      const defaultValue = this.formGroup.controls[Controls.WorkmanCompensationDocumentation].value;
      this.onWorkmenCompensationLookUpValueSelected(defaultValue);
    }
    else if (type === DocumentationType.RoadAccidentFundDocumentation) {
      this.patchDefaultRoadAccidentDocumentationValue();
      this.disableUploadBtn = false;

      // Update `description` byRef for fileUploadConfig
      const defaultValue = this.formGroup.controls[Controls.RoadAccidentFundDocumentation].value;
      this.onRoadAccidentLookUpValueSelected(defaultValue);
    }
  }

  onClearDescription(e): void {
    e.preventDefault();
    this.formGroup.controls[Controls.Description].reset();
    this.disableUploadBtn = true;
  }

  patchDefaultGeneralDocumentationValue(): void {
    this.formGroup.patchValue({
      [this.controls.DocumentationType]: this.documentationType.GeneralDocumentation,
      [this.controls.Description]: null
    });

    // Disabled button on success:
    this.disableUploadBtn = true;
  }

  patchDefaultWorkmenDocumentationValue(): void {
    this.formGroup.patchValue({
      [this.controls.DocumentationType]: this.documentationType.WorkmenCompensationDocumentation,
      [this.controls.WorkmanCompensationDocumentation]: this.workmanCompensationDocumentationLookUpValues[0].value // Bind First Default
      // Value
    });
  }

  patchDefaultRoadAccidentDocumentationValue(): void {
    this.formGroup.patchValue({
      [this.controls.DocumentationType]: this.documentationType.RoadAccidentFundDocumentation,
      [this.controls.RoadAccidentFundDocumentation]: this.roadAccidentFundDocumentationLookUpValues[0].value // Bind First Default Value
    });
  }

  onFileUploadSuccess(uploadedFileResponse: any): void {
    if (!uploadedFileResponse) {
      return;
    }

    this.setFileUpload(uploadedFileResponse);
    //  Create new "CaseHospitalFileLinkDTO" payload and push into list:
    const caseHospitalFileLinkDTOList = this.updateCaseHospitalFileLinkDTOList();
    // Add up All list to maintain `caseHospitalFiles` local state upto date
    this.addAllCaseHospitalFileLinkDTO();

    if (this.isEditMode) {
      // AdHoc requirement to upload document on Edit Case `/document` endpoint
      this.uploadDocument.emit(caseHospitalFileLinkDTOList);
    }
  }

  updateCaseHospitalFileLinkDTOList(): CaseHospitalFileLinkDTO {
    const documentationTypeSelected = this.formGroup.controls[Controls.DocumentationType].value;
    const newItem: CaseHospitalFileLinkDTO = this.getCaseHospitalFileLinkDTOPayload();

    if (documentationTypeSelected === DocumentationType.GeneralDocumentation) {
      this.patchDefaultGeneralDocumentationValue();
      this.generalDocumentation.push(newItem);
    }
    else if (documentationTypeSelected === DocumentationType.WorkmenCompensationDocumentation) {
      this.patchDefaultWorkmenDocumentationValue();
      this.workmanCompensationDocumentation.push(newItem);
    }
    else if (documentationTypeSelected === DocumentationType.RoadAccidentFundDocumentation) {
      this.patchDefaultRoadAccidentDocumentationValue();
      this.roadAccidentFundDocumentation.push(newItem);
    }
    return newItem;
  }

  getPayload(): { caseHospitalFiles: CaseHospitalFileLinkDTO[] } {

    /*
     Note:
     clear base64Data file’s byte array. The file’s byte array was already
     posted to the server. Backend will link "fileId" to posted file + this
     will help to avoid unnecessarily big payload posted to Backend.
     */
    const documentationList = [
      ...this.generalDocumentation,
      ...this.workmanCompensationDocumentation,
      ...this.roadAccidentFundDocumentation
    ].map((item: CaseHospitalFileLinkDTO) => {
      const {file} = item;
      if (file.file) {
        file.file = null;
      }
      return item;
    });

    return {
      caseHospitalFiles: documentationList
    };
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onDeleteDocumentation($event: MouseEvent, type: DocumentationType, document: CaseHospitalFileLinkDTO): void {
    $event.stopPropagation();

    const filename = `${document.file.name}${document.file.extension}`;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you want to delete ${filename}?`,
      panelClass: 'white-space-none'
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.onDeleteDocumentationConfirmed(type, document);
        }
        else {
          dialogRef.close();
        }
      });
  }

  private onDeleteDocumentationConfirmed(type: DocumentationType, document: CaseHospitalFileLinkDTO): void {
    let documentationList: CaseHospitalFileLinkDTO[];
    let patchFunction: () => void;

    switch (type) {
    case DocumentationType.GeneralDocumentation:
      documentationList = this.generalDocumentation;
      patchFunction = () => this.patchDefaultGeneralDocumentationValue();
      break;
    case DocumentationType.WorkmenCompensationDocumentation:
      documentationList = this.workmanCompensationDocumentation;
      patchFunction = () => this.patchDefaultWorkmenDocumentationValue();
      break;
    case DocumentationType.RoadAccidentFundDocumentation:
      documentationList = this.roadAccidentFundDocumentation;
      patchFunction = () => this.patchDefaultRoadAccidentDocumentationValue();
      break;
    default:
      return;
    }

    const fileId: bigint = document.file.fileId;
    const fileIndex: number = documentationList.findIndex((item: CaseHospitalFileLinkDTO) => item.file.fileId === fileId);

    if (fileIndex > -1) {
      documentationList.splice(fileIndex, 1);
      patchFunction();
      this.onDeleteFile(fileId);
    }
  }

  private onDeleteFile(fileId: bigint): void {
    if (this.isEditMode) {
      this.deleteDocument.next({caseHospitalId: this.caseHospitalId, fileId});
    }
    else {
      this.deleteFile$(fileId)
        .pipe(
          finalize(() => {
            this.deleteLocalFileState();

            // Case Add/Edit
            // Once file is deleted update list:
            // Update "caseHospitalFiles" with new list items per list
            this.addAllCaseHospitalFileLinkDTO();
          })
        )
        .subscribe({
          next: () => {
            //  Empty Subscription
          },
          error: (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.message);
            console.log(`File with ${fileId} deleted from Angular local state not from server!`);
          }
        });
    }

  }

  private deleteFile$(fileId: bigint): Observable<any> {
    return new Observable(observer => {
      this.fileUploadService.deleteFile(fileId)
        .pipe(
          take(1),
          finalize(() => {
            observer.complete();
          })
        )
        .subscribe(
          () => {
            observer.next({
              success: true
            });
          },
          (error) => {
            observer.next({success: false, error});
          }
        );
    });
  }

  private deleteLocalFileState(): void {
    this.clearFileUpload();
    this.sharedService.openSnackBar(`File deleted successfully!`, 'Dismiss');
  }

  private clearFileUpload(): void {
    this.fileUploadConfig.uploadedFileData = null;
  }

  private setFileUpload(file: any): void {
    this.fileUploadConfig.uploadedFileData = file;
  }

  private getCaseHospitalFileLinkDTOPayload(): CaseHospitalFileLinkDTO {

    const caseHospitalFileLinkDTO: CaseHospitalFileLinkDTO = new CaseHospitalFileLinkDTO();
    caseHospitalFileLinkDTO.caseHospitalId = this.isEditMode ? this.caseHospitalId : null;
    caseHospitalFileLinkDTO.created = new Date();
    caseHospitalFileLinkDTO.createdBy = this.loggedInUserName;
    caseHospitalFileLinkDTO.updated = null;
    caseHospitalFileLinkDTO.updatedBy = null;
    caseHospitalFileLinkDTO.tenantId = null;
    caseHospitalFileLinkDTO.file = this.getAttachmentPayload();

    // Note: Get Value of "Description" based on Documentation Type Selected
    const documentationTypeSelected = this.formGroup.controls[Controls.DocumentationType].value;

    if (documentationTypeSelected === DocumentationType.GeneralDocumentation) {
      caseHospitalFileLinkDTO.workmanCompensationFundDocumentation = null;
      caseHospitalFileLinkDTO.accidentRoadFundDocumentation = null;
    }
    else if (documentationTypeSelected === DocumentationType.WorkmenCompensationDocumentation) {
      let value: any;
      value = this.formGroup.controls[Controls.WorkmanCompensationDocumentation].value;
      caseHospitalFileLinkDTO.workmanCompensationFundDocumentation = this.workmanCompensationDocumentationLookUpValues.find(item => item.value === value);
      caseHospitalFileLinkDTO.accidentRoadFundDocumentation = null;
    }
    else if (documentationTypeSelected === DocumentationType.RoadAccidentFundDocumentation) {
      let value: any;
      value = this.formGroup.controls[Controls.RoadAccidentFundDocumentation].value;
      caseHospitalFileLinkDTO.workmanCompensationFundDocumentation = null;
      caseHospitalFileLinkDTO.accidentRoadFundDocumentation = this.roadAccidentFundDocumentationLookUpValues.find(item => item.value === value);
    }

    return caseHospitalFileLinkDTO;
  }

  private getAttachmentPayload(): FileDTO {
    const {uploadedFileData} = this.fileUploadConfig;
    if (!uploadedFileData && uploadedFileData.file) {
      return;
    }
    const fileDTO: FileDTO = new FileDTO();
    fileDTO.created = new Date();
    fileDTO.createdBy = this.loggedInUserName;
    fileDTO.updated = null;
    fileDTO.updatedBy = null;
    fileDTO.tenantId = null;

    fileDTO.fileId = uploadedFileData.fileId;
    fileDTO.name = uploadedFileData.name;
    fileDTO.file = uploadedFileData.file;
    fileDTO.extension = uploadedFileData.extension;

    // Note: Get Value of "Description" based on Documentation Type Selected
    const documentationTypeSelected = this.formGroup.controls[Controls.DocumentationType].value;
    if (documentationTypeSelected === DocumentationType.GeneralDocumentation) {
      fileDTO.description = this.formGroup.controls[Controls.Description].value;
    }
    else if (documentationTypeSelected === DocumentationType.WorkmenCompensationDocumentation) {
      const selectedDocumentation = this.formGroup.controls[Controls.WorkmanCompensationDocumentation].value;
      const matchingValue = this.workmanCompensationDocumentationLookUpValues.find((item: NameValueDTO) => item.value === selectedDocumentation);
      fileDTO.description = matchingValue ? matchingValue.name : null;
    }
    else if (documentationTypeSelected === DocumentationType.RoadAccidentFundDocumentation) {
      const selectedDocumentation = this.formGroup.controls[Controls.RoadAccidentFundDocumentation].value;
      const matchingValue = this.roadAccidentFundDocumentationLookUpValues.find((item: NameValueDTO) => item.value === selectedDocumentation);
      fileDTO.description = matchingValue ? matchingValue.name : null;
    }
    return fileDTO;
  }

  private setFormsForEdit(data: CaseHospitalDTO): void {
    const {caseHospitalFiles} = data;
    if (!caseHospitalFiles) {
      return;
    }

    // Note: Filter list based on conditions
    //  General Documentation
    const generalDocumentationList = caseHospitalFiles.filter((item: CaseHospitalFileLinkDTO) => {
      const {workmanCompensationFundDocumentation, accidentRoadFundDocumentation} = item;
      return workmanCompensationFundDocumentation === null && accidentRoadFundDocumentation === null;
    });

    //  Workmen Documentation
    const workmanDocumentationList = caseHospitalFiles.filter((item: CaseHospitalFileLinkDTO) => {
      const {workmanCompensationFundDocumentation, accidentRoadFundDocumentation} = item;
      return workmanCompensationFundDocumentation !== null && accidentRoadFundDocumentation === null;
    });

    //  Road Accident Documentation
    const roadAccidentDocumentationList = caseHospitalFiles.filter((item: CaseHospitalFileLinkDTO) => {
      const {workmanCompensationFundDocumentation, accidentRoadFundDocumentation} = item;
      return workmanCompensationFundDocumentation === null && accidentRoadFundDocumentation !== null;
    });

    if (generalDocumentationList.length > 0) {
      this.generalDocumentation = generalDocumentationList;
    }
    if (workmanDocumentationList.length > 0) {
      this.workmanCompensationDocumentation = workmanDocumentationList;
    }
    if (roadAccidentDocumentationList.length > 0) {
      this.roadAccidentFundDocumentation = roadAccidentDocumentationList;
    }

    // Add up All list to maintain `caseHospitalFiles` local state upto date
    this.addAllCaseHospitalFileLinkDTO();
  }

  private addAllCaseHospitalFileLinkDTO(): void {
    this.caseHospitalFiles = [].concat(
      this.generalDocumentation,
      this.workmanCompensationDocumentation,
      this.roadAccidentFundDocumentation
    );
  }

  private removeAllCaseHospitalFileLinkDTO(): void {
    this.generalDocumentation = [];
    this.roadAccidentFundDocumentation = [];
    this.workmanCompensationDocumentation = [];
    this.caseHospitalFiles = [];
  }
}
